import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import type { TextFieldProps } from '@mui/material/TextField';
import Button from '@mui/material/Button';
import type { ButtonProps } from '@mui/material/Button';

import { calculateTimeDifference } from 'services';
import { AM, PM, HOUR_IN_MINUTES, levels } from 'consts';
import { Levels } from 'enums';

import { validateCustomRemoteLevel } from './validation';
import { testIsNumber } from './utils';

import type { RemoteLevel, AmPmHourValues, AmPmValues } from 'types';

interface RemoteLevelTileProps {
  changeSelectedRemoteLevel: (remoteLevel: RemoteLevel) => void;
}

interface CustomRemoteLevelState {
  allowedOverlap: number | '';
  workingDayStartAmPm: AmPmValues;
  workingDayStartHours: AmPmHourValues;
  workingDayEndAmPm: AmPmValues;
  workingDayEndHours: AmPmHourValues;
}

export const CustomRemoteLevel = ({
  changeSelectedRemoteLevel,
}: RemoteLevelTileProps) => {
  const [customRemoteLevel, setCustomRemoteLevel] =
    useState<CustomRemoteLevelState>({
      allowedOverlap: 2,
      workingDayStartAmPm: AM,
      workingDayStartHours: 8,
      workingDayEndAmPm: PM,
      workingDayEndHours: 8,
    });

  // just to not repeat those props in JSX
  const createAmPmButtonProps = (
    AmPmValue: AmPmValues,
    workingDayAmPmKey: 'workingDayStartAmPm' | 'workingDayEndAmPm'
  ): ButtonProps => ({
    size: 'small',
    color: 'primary',
    sx: {
      ml: 4,
      mt: 2,
      color:
        customRemoteLevel[workingDayAmPmKey] === AmPmValue
          ? 'grey.A100'
          : 'text.regular',
      fontWeight:
        customRemoteLevel[workingDayAmPmKey] === AmPmValue ? 700 : 500,
    },
    variant:
      customRemoteLevel[workingDayAmPmKey] === AmPmValue ? 'contained' : 'text',
    onClick: () => {
      setCustomRemoteLevel((prev) => ({
        ...prev,
        [workingDayAmPmKey]: AmPmValue,
      }));
    },
  });

  // just to not repeat those props in JSX
  const createAmPmTimeInputProps = <ChangeValue,>(
    customRemoteLevelInputField:
      | 'workingDayStartHours'
      | 'workingDayEndHours'
      | 'allowedOverlap'
  ): TextFieldProps => ({
    autoComplete: 'off',
    fullWidth: true,
    type: 'text',
    value: !customRemoteLevel[customRemoteLevelInputField]
      ? ''
      : customRemoteLevel[customRemoteLevelInputField],
    onChange: ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      const isNumber = testIsNumber(value);
      const isEmpty = value === '';
      const matchesMaxLength = value.length <= 2;
      const isInDayHoursRange =
        (Number(value) >= 1 && Number(value) <= 12) || isEmpty;

      if ((isNumber || isEmpty) && matchesMaxLength && isInDayHoursRange)
        setCustomRemoteLevel((prev) => ({
          ...prev,
          [customRemoteLevelInputField]: Number(
            value
          ) as unknown as ChangeValue,
        }));
    },
  });

  const workingHoursCount = calculateTimeDifference({
    workingDayStartAmPm: {
      hours: customRemoteLevel.workingDayStartHours,
      ampm: customRemoteLevel.workingDayStartAmPm,
    },
    workingDayEndAmPm: {
      hours: customRemoteLevel.workingDayEndHours,
      ampm: customRemoteLevel.workingDayEndAmPm,
    },
    unit: 'hour',
  });

  const errors = validateCustomRemoteLevel(
    customRemoteLevel,
    workingHoursCount
  );
  const isValid = Object.values(errors).every((error) => !error);

  return (
    <Box display="flex" flexDirection="column">
      <Typography
        variant="textMedium"
        color="text.secondary"
        mb={5}
        fontWeight={500}
      >
        My day starts at:
      </Typography>
      <Box display="flex" mb={4} alignItems="flex-start">
        <TextField
          {...createAmPmTimeInputProps<AmPmHourValues>('workingDayStartHours')}
          error={!!errors.workingDayStartHours}
          helperText={errors.workingDayStartHours}
          placeholder="start time"
        />
        <Button {...createAmPmButtonProps(AM, 'workingDayStartAmPm')}>
          AM
        </Button>
        <Button {...createAmPmButtonProps(PM, 'workingDayStartAmPm')}>
          PM
        </Button>
      </Box>

      <Typography
        variant="textMedium"
        color="text.secondary"
        mb={5}
        fontWeight={500}
      >
        and ends at:
      </Typography>
      <Box display="flex" mb={4} alignItems="flex-start">
        <TextField
          {...createAmPmTimeInputProps<AmPmHourValues>('workingDayEndHours')}
          error={!!errors.workingDayEndHours && !errors.workingDayStartHours}
          helperText={!errors.workingDayStartHours && errors.workingDayEndHours}
          placeholder="end time"
        />
        <Button {...createAmPmButtonProps(AM, 'workingDayEndAmPm')}>AM</Button>
        <Button {...createAmPmButtonProps(PM, 'workingDayEndAmPm')}>PM</Button>
      </Box>

      <Typography
        variant="textMedium"
        color="text.secondary"
        mb={5}
        fontWeight={500}
      >
        Overlap hours{' '}
        {!errors.workingDayStartHours && !errors.workingDayEndHours
          ? `(working day lasts ${workingHoursCount} hours)`
          : ''}
      </Typography>

      <TextField
        {...createAmPmTimeInputProps<number>('allowedOverlap')}
        error={
          !!errors.allowedOverlap &&
          !errors.workingDayEndHours &&
          !errors.workingDayStartHours
        }
        helperText={
          !errors.workingDayEndHours &&
          !errors.workingDayStartHours &&
          errors.allowedOverlap
        }
        value={customRemoteLevel.allowedOverlap}
        onChange={({
          target: { value },
        }: React.ChangeEvent<HTMLInputElement>) => {
          const isNumber = testIsNumber(value);
          const isEmpty = value === '';
          const isInDayHoursRange =
            (Number(value) >= 0 && Number(value) <= 23) || isEmpty;

          if ((isNumber || isEmpty) && isInDayHoursRange)
            setCustomRemoteLevel((prev) => ({
              ...prev,
              allowedOverlap: isEmpty ? '' : Number(value),
            }));
        }}
        placeholder="overlap hours"
        sx={{ mb: 5 }}
      />

      <Button
        size="small"
        variant="contained"
        disabled={!isValid}
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => {
          if (customRemoteLevel.allowedOverlap === '') return;

          const defaultCustomLevelConfig = levels.find(
            (remoteLevel) => remoteLevel.level === Levels.Six
          ) as RemoteLevel;

          changeSelectedRemoteLevel({
            ...defaultCustomLevelConfig,
            allowedOverlap: customRemoteLevel.allowedOverlap * HOUR_IN_MINUTES,
            workingDayStartAmPm: {
              hours: customRemoteLevel.workingDayStartHours,
              ampm: customRemoteLevel.workingDayStartAmPm,
            },
            workingDayEndAmPm: {
              hours: customRemoteLevel.workingDayEndHours,
              ampm: customRemoteLevel.workingDayEndAmPm,
            },
          });
        }}
      >
        Apply
      </Button>
    </Box>
  );
};
